import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useCallback, useEffect, useMemo, useState } from "react";
import { foyerApi } from "../http/foyer";
import { tokens } from "../theme";
import { platformLabel } from "../utils/devicePresentation";
import { PageContainer, PageHeader } from "../components/PageHeader";
import type {
  DeviceGroup,
  DevicePlatform,
  FleetDevice,
  OtaKind,
  Rollout,
  RolloutAdoption,
  RolloutStatus,
} from "@shared/types/foyer";

function deviceLabel(d: FleetDevice): string {
  const where = [d.roomId, d.premiseName].filter(Boolean).join(" · ");
  return where || d.id;
}

const PLATFORMS: DevicePlatform[] = [
  "web",
  "stb",
  "philips",
  "tizen",
  "webos",
  "ios",
];

/** A rollout status as a tinted chip. */
function StatusChip({ status }: { status: RolloutStatus }) {
  const map: Record<RolloutStatus, { label: string; bg: string; fg: string }> =
    {
      draft: { label: "Draft", bg: tokens.rowAlt, fg: tokens.inkMuted },
      canary: { label: "Canary", bg: tokens.warningTint, fg: tokens.warning },
      rolling: {
        label: "Rolling",
        bg: tokens.warningTint,
        fg: tokens.warning,
      },
      paused: { label: "Paused", bg: tokens.rowAlt, fg: tokens.inkMuted },
      completed: {
        label: "Completed",
        bg: tokens.successTint,
        fg: tokens.success,
      },
      rolledBack: {
        label: "Rolled back",
        bg: tokens.rowAlt,
        fg: tokens.inkMuted,
      },
    };
  const s = map[status];
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{ bgcolor: s.bg, color: s.fg, fontWeight: 600 }}
    />
  );
}

/** The state-machine actions available from a given status. */
function actionsFor(
  status: RolloutStatus
): Array<"start" | "advance" | "pause" | "resume" | "rollback"> {
  switch (status) {
    case "draft":
      return ["start", "rollback"];
    case "canary":
      return ["advance", "pause", "rollback"];
    case "rolling":
      return ["pause", "rollback"];
    case "paused":
      return ["resume", "rollback"];
    default:
      return [];
  }
}

const ACTION_LABEL: Record<string, string> = {
  start: "Start canary",
  advance: "Roll out to all",
  pause: "Pause",
  resume: "Resume",
  rollback: "Roll back",
};

const KIND_LABEL: Record<OtaKind, string> = {
  web_bundle: "Web bundle",
  apk: "Native APK",
  firmware: "OEM firmware",
};

/** Firmware can't be un-flashed, so its "rollback" only halts further devices. */
function actionLabel(action: string, rollout: Rollout): string {
  if (action === "rollback" && rollout.kind === "firmware") return "Abort";
  return ACTION_LABEL[action];
}

/** One-line description of what a rollout does - OTA target, or the bulk action. */
function rolloutSummary(r: Rollout): string {
  if (r.action === "reboot") return "Reboot";
  if (r.action === "message") {
    const body = r.message?.body ?? "";
    return `Message${body ? `: "${body}"` : ""}`;
  }
  const kind = r.kind ? KIND_LABEL[r.kind] : "Update";
  return `${kind} → ${r.targetVersion ?? "-"}`;
}

/** How a rollout's adoption reads: OTA converges on a version; bulk actions
 *  converge on the device acking the command. */
function adoptionSummary(r: Rollout, a: RolloutAdoption): string {
  const goal =
    r.action === "ota" ? `on ${r.targetVersion ?? "target"}` : "acked";
  return `${a.atTarget}/${a.total} ${goal} · ${a.dispatched} dispatched`;
}

function selectorSummary(r: Rollout, groupName?: string): string {
  const parts: string[] = [];
  if (r.groupId) parts.push(`group: ${groupName ?? r.groupId}`);
  if (r.premiseId) parts.push(r.premiseId);
  if (r.platform) parts.push(platformLabel(r.platform));
  return parts.length ? parts.join(" · ") : "All devices";
}

function RolloutCard({
  rollout,
  adoption,
  groupName,
  onAction,
}: {
  rollout: Rollout;
  adoption?: RolloutAdoption;
  groupName?: string;
  onAction: (
    id: string,
    action: "start" | "advance" | "pause" | "resume" | "rollback"
  ) => void;
}) {
  const pct = adoption?.pct ?? 0;
  // APK waves only: devices that install but need an on-screen confirmation
  // (no `otaApkSilent`). Worth surfacing - those devices stall until someone
  // walks into the room, which otherwise just looks like stuck adoption.
  const promptCount =
    rollout.kind === "apk"
      ? (adoption?.devices.filter((d) => d.needsPrompt).length ?? 0)
      : 0;
  return (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
        <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {rollout.name}
        </Typography>
        <StatusChip status={rollout.status} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {rolloutSummary(rollout)} · {selectorSummary(rollout, groupName)}
      </Typography>
      {promptCount > 0 && (
        <Typography variant="caption" color="warning.main" display="block">
          {promptCount} of {adoption?.total ?? 0} device
          {promptCount === 1 ? "" : "s"} can&apos;t install silently - the build
          downloads over the air, then waits for someone to confirm the install
          prompt on screen. These boxes are not enrolled as device owner; a
          factory reset plus enrolment at the next site visit is what removes
          the prompt for good.
        </Typography>
      )}
      {rollout.ntpServer && (
        <Typography variant="caption" color="text.secondary">
          NTP server → <strong>{rollout.ntpServer}</strong>
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Adoption
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {adoption ? adoptionSummary(rollout, adoption) : "-"}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
        {actionsFor(rollout.status).map((a) => (
          <Button
            key={a}
            size="small"
            variant={a === "rollback" ? "text" : "outlined"}
            color={a === "rollback" ? "error" : "primary"}
            onClick={() => onAction(rollout.rolloutId, a)}
          >
            {actionLabel(a, rollout)}
          </Button>
        ))}
        {actionsFor(rollout.status).length === 0 && (
          <Typography variant="caption" color="text.secondary">
            {rollout.status === "completed"
              ? rollout.action === "ota"
                ? "All targeted devices are on the new version."
                : "All targeted devices have completed the action."
              : "No further actions."}
          </Typography>
        )}
      </Stack>
    </Card>
  );
}

interface DraftForm {
  name: string;
  kind: OtaKind;
  targetVersion: string;
  premiseId: string;
  platform: DevicePlatform | "";
  groupId: string;
  canaryPct: number;
  artifactUrl: string;
  fileName: string;
  sizeBytes: string;
  ntpServer: string;
}

const EMPTY_DRAFT: DraftForm = {
  name: "",
  kind: "web_bundle",
  targetVersion: "",
  premiseId: "",
  platform: "",
  groupId: "",
  canaryPct: 10,
  artifactUrl: "",
  fileName: "",
  sizeBytes: "",
  ntpServer: "",
};

export default function OtaPage() {
  const [rollouts, setRollouts] = useState<Rollout[]>([]);
  const [adoption, setAdoption] = useState<Record<string, RolloutAdoption>>({});
  const [groups, setGroups] = useState<DeviceGroup[]>([]);
  const [devices, setDevices] = useState<FleetDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<DraftForm>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [uploadingFw, setUploadingFw] = useState(false);

  // Upload an OEM firmware image: hosts it at /fw/<id>.<ext> and auto-fills the
  // rollout's URL / file name / size from the one upload.
  const onFirmwareFile = useCallback(async (file: File | undefined | null) => {
    if (!file) return;
    setUploadingFw(true);
    setError(null);
    try {
      const fw = await foyerApi.uploadFirmware(file);
      setDraft((d) => ({
        ...d,
        artifactUrl: fw.url,
        fileName: fw.originalName,
        sizeBytes: String(fw.size),
      }));
    } catch {
      setError("Firmware upload failed.");
    } finally {
      setUploadingFw(false);
    }
  }, []);

  useEffect(() => {
    foyerApi
      .listDeviceGroups()
      .then((g) => setGroups(g.groups))
      .catch(() => {});
    foyerApi
      .fleetDevices()
      .then((f) => setDevices(f.devices))
      .catch(() => {});
  }, []);

  // Member preview for the currently-picked group in the create dialog.
  const pickedGroup = useMemo(
    () => groups.find((g) => g.groupId === draft.groupId),
    [groups, draft.groupId]
  );
  const groupPreview = useMemo(() => {
    if (!pickedGroup) return null;
    const byId = new Map(devices.map((d) => [d.id, d]));
    const members = pickedGroup.screenIds.map((id) => byId.get(id));
    const known = members.filter((d): d is FleetDevice => Boolean(d));
    return {
      total: pickedGroup.screenIds.length,
      online: known.filter((d) => d.online).length,
      labels: known.map(deviceLabel),
    };
  }, [pickedGroup, devices]);

  // How the fleet would take an APK wave today, from the last heartbeat's
  // reported capabilities: silent (unattended) / prompted (needs a human) /
  // no native installer at all (excluded from APK cohorts server-side).
  const apkCapability = useMemo(() => {
    let silent = 0;
    let prompt = 0;
    let unsupported = 0;
    for (const d of devices) {
      const caps = d.live?.capabilities ?? [];
      if (!caps.includes("otaApk")) unsupported += 1;
      else if (caps.includes("otaApkSilent")) silent += 1;
      else prompt += 1;
    }
    return { silent, prompt, unsupported };
  }, [devices]);

  /**
   * Devices whose native container we CANNOT push an update to (TIZEN-13).
   *
   * A Samsung panel updates its widget by re-reading `sssp_config.xml` and
   * deciding for itself - there is no command we can send and no cohort we can
   * hold back, so every panel pointed at that file takes the update at once.
   * A canary slider over those devices is a promise of control the platform
   * does not give us, and the honest fix is to SAY so rather than to model
   * per-cohort config URLs we would then have to maintain.
   *
   * Listed with the widget version each one is actually on, because that is the
   * question a canary was standing in for: "which of my panels is stale?"
   */
  const platformAppDevices = useMemo(
    () =>
      devices.filter((d) => d.live?.capabilities?.includes("otaPlatformApp")),
    [devices]
  );
  const platformAppVersions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of platformAppDevices) {
      const v = d.live?.appVersion || "unknown";
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [platformAppDevices]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { rollouts: rs } = await foyerApi.listRollouts();
      setRollouts(rs);
      // Adoption comes from the detail endpoint (cohort ⋈ reported version).
      const details = await Promise.all(
        rs.map((r) =>
          foyerApi
            .rollout(r.rolloutId)
            .then((d) => [r.rolloutId, d.adoption] as const)
            .catch(() => null)
        )
      );
      const next: Record<string, RolloutAdoption> = {};
      for (const d of details) if (d) next[d[0]] = d[1];
      setAdoption(next);
    } catch {
      setError("Failed to load rollouts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onAction = useCallback(
    async (
      id: string,
      action: "start" | "advance" | "pause" | "resume" | "rollback"
    ) => {
      try {
        await foyerApi.rolloutAction(id, action);
        await load();
      } catch {
        setError(`Action "${action}" failed.`);
      }
    },
    [load]
  );

  const needsArtifact = draft.kind === "apk" || draft.kind === "firmware";
  const canCreate = useMemo(
    () =>
      draft.name.trim() !== "" &&
      draft.targetVersion.trim() !== "" &&
      (!needsArtifact || draft.artifactUrl.trim() !== ""),
    [draft, needsArtifact]
  );

  const create = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await foyerApi.createRollout({
        name: draft.name.trim(),
        kind: draft.kind,
        targetVersion: draft.targetVersion.trim(),
        premiseId: draft.premiseId.trim() || undefined,
        platform: draft.platform || undefined,
        groupId: draft.groupId || undefined,
        canaryPct: draft.canaryPct,
        artifactUrl:
          draft.kind === "apk" || draft.kind === "firmware"
            ? draft.artifactUrl.trim()
            : undefined,
        fileName:
          draft.kind === "firmware" && draft.fileName.trim()
            ? draft.fileName.trim()
            : undefined,
        sizeBytes:
          draft.kind === "firmware" && draft.sizeBytes.trim()
            ? Number(draft.sizeBytes) || undefined
            : undefined,
        ntpServer: draft.ntpServer.trim() || undefined,
      });
      setDialogOpen(false);
      setDraft(EMPTY_DRAFT);
      await load();
    } catch {
      setError("Failed to create the rollout.");
    } finally {
      setSaving(false);
    }
  }, [draft, load]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Updates"
        title="OTA Rollouts"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            New rollout
          </Button>
        }
      />

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ mt: 3 }}>
        {loading ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : rollouts.length === 0 ? (
          <Card
            sx={{
              p: 5,
              textAlign: "center",
              color: tokens.inkMuted,
            }}
          >
            <RocketLaunchOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography sx={{ mt: 1 }}>No rollouts yet.</Typography>
            <Typography variant="body2">
              Create one to push a new version to a slice of the fleet.
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {rollouts.map((r) => (
              <Grid item key={r.rolloutId} xs={12} md={6}>
                <RolloutCard
                  rollout={r}
                  adoption={adoption[r.rolloutId]}
                  groupName={groups.find((g) => g.groupId === r.groupId)?.name}
                  onAction={onAction}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New OTA rollout</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              fullWidth
              autoFocus
            />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Kind</InputLabel>
                <Select
                  label="Kind"
                  value={draft.kind}
                  onChange={(e) =>
                    setDraft({ ...draft, kind: e.target.value as OtaKind })
                  }
                >
                  <MenuItem value="web_bundle">
                    Web bundle (all platforms)
                  </MenuItem>
                  <MenuItem value="apk">Native APK (Android)</MenuItem>
                  <MenuItem value="firmware">OEM firmware (TV)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Target version"
                value={draft.targetVersion}
                onChange={(e) =>
                  setDraft({ ...draft, targetVersion: e.target.value })
                }
                placeholder="0.3.0"
                fullWidth
              />
            </Stack>

            {draft.kind === "apk" && (
              <>
                <TextField
                  label="APK download URL"
                  value={draft.artifactUrl}
                  onChange={(e) =>
                    setDraft({ ...draft, artifactUrl: e.target.value })
                  }
                  placeholder="https://foyer.nenda.com/apk/foyer-0.3.0.apk"
                  fullWidth
                />
                <Typography variant="caption" color="text.secondary">
                  Each Android shell downloads the APK and installs it over
                  itself. Devices provisioned as device owner (or on a
                  system-signed build) install <strong>silently</strong>;
                  everywhere else the TV shows the system install prompt and
                  waits for someone in the room. Devices with no native
                  installer aren&apos;t targeted at all - use a web-bundle
                  rollout for those.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Fleet right now: <strong>{apkCapability.silent}</strong>{" "}
                  silent · <strong>{apkCapability.prompt}</strong> prompted ·{" "}
                  <strong>{apkCapability.unsupported}</strong> can&apos;t
                  install.
                </Typography>
              </>
            )}

            {draft.kind === "firmware" && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Point each TV at the OEM firmware image (Philips .upg /
                  Samsung .bem) - its own updater downloads and flashes it.
                  Paste a URL the TVs can reach, or upload the file to host it
                  here. No rollback once flashed; the canary wave is your safety
                  net.
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    disabled={uploadingFw}
                  >
                    {uploadingFw ? "Uploading…" : "Upload firmware"}
                    <input
                      type="file"
                      hidden
                      onChange={(e) => {
                        void onFirmwareFile(e.target.files?.[0]);
                        // Allow re-selecting the same file later.
                        e.target.value = "";
                      }}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Hosted at /fw/… and pasted in below (up to 1 GB).
                  </Typography>
                </Stack>
                {uploadingFw && <LinearProgress />}
                <TextField
                  label="Firmware image URL"
                  value={draft.artifactUrl}
                  onChange={(e) =>
                    setDraft({ ...draft, artifactUrl: e.target.value })
                  }
                  placeholder="https://foyer.nenda.com/fw/abc123.upg"
                  fullWidth
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Image file name"
                    placeholder="swuimage.bem"
                    value={draft.fileName}
                    onChange={(e) =>
                      setDraft({ ...draft, fileName: e.target.value })
                    }
                    fullWidth
                  />
                  <TextField
                    label="Size (bytes)"
                    type="number"
                    value={draft.sizeBytes}
                    onChange={(e) =>
                      setDraft({ ...draft, sizeBytes: e.target.value })
                    }
                    fullWidth
                  />
                </Stack>
              </>
            )}

            <Typography variant="caption" color="text.secondary">
              Target cohort (leave blank for the whole fleet)
            </Typography>
            {groups.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Device group</InputLabel>
                <Select
                  label="Device group"
                  value={draft.groupId}
                  onChange={(e) =>
                    // A group is a fixed cohort, so it overrides the
                    // premise/platform filters - clear them when one is picked.
                    setDraft({
                      ...draft,
                      groupId: e.target.value,
                      ...(e.target.value
                        ? { premiseId: "", platform: "" }
                        : {}),
                    })
                  }
                >
                  <MenuItem value="">No group (use filters below)</MenuItem>
                  {groups.map((g) => (
                    <MenuItem key={g.groupId} value={g.groupId}>
                      {g.name} ({g.screenIds.length})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {groupPreview && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: tokens.brandTintSoft,
                  border: `1px solid ${tokens.brandTint}`,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Targeting <strong>{groupPreview.total}</strong> device
                  {groupPreview.total === 1 ? "" : "s"} in this group
                  {devices.length > 0
                    ? ` · ${groupPreview.online} online now`
                    : ""}
                </Typography>
                {groupPreview.labels.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ flexWrap: "wrap", gap: 0.75, mt: 1 }}
                  >
                    {groupPreview.labels.slice(0, 6).map((label, i) => (
                      <Chip
                        key={i}
                        size="small"
                        variant="outlined"
                        label={label}
                        sx={{ maxWidth: 220 }}
                      />
                    ))}
                    {groupPreview.labels.length > 6 && (
                      <Chip
                        size="small"
                        label={`+${groupPreview.labels.length - 6} more`}
                        sx={{ bgcolor: tokens.rowAlt, color: tokens.inkMuted }}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            )}

            {draft.groupId ? (
              <Typography variant="caption" color="text.secondary">
                Premise &amp; platform filters are ignored while a group is
                selected.
              </Typography>
            ) : null}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Premise slug"
                value={draft.premiseId}
                disabled={!!draft.groupId}
                onChange={(e) =>
                  setDraft({ ...draft, premiseId: e.target.value })
                }
                fullWidth
              />
              <FormControl fullWidth disabled={!!draft.groupId}>
                <InputLabel>Platform</InputLabel>
                <Select
                  label="Platform"
                  value={draft.platform}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      platform: e.target.value as DevicePlatform | "",
                    })
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  {PLATFORMS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {platformLabel(p)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Canary % is an arbitrary slice of the cohort, taken in a stable
              order: &ldquo;Start canary&rdquo; sends to it, &ldquo;Roll out to
              all&rdquo; widens to the rest. To choose <em>which</em> devices go
              first - a test room, a friendly site - target a device group
              instead and roll that one out ahead of the fleet.
            </Typography>
            <TextField
              label="Canary %"
              type="number"
              value={draft.canaryPct}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  canaryPct: Number(e.target.value) || 0,
                })
              }
              inputProps={{ min: 1, max: 100 }}
              sx={{ width: 200 }}
            />

            {/* The canary cannot hold back a widget update, so say which
                devices it will not apply to and answer the question it was
                standing in for instead. See platformAppDevices. */}
            {platformAppDevices.length > 0 && (
              <Typography
                variant="caption"
                color="warning.main"
                data-testid="ota-platform-app-note"
              >
                Staged rollout is Android only.{" "}
                <strong>{platformAppDevices.length}</strong> device
                {platformAppDevices.length === 1 ? "" : "s"} in this fleet
                update their native container from <code>sssp_config.xml</code>{" "}
                and decide for themselves, so a canary cannot hold them back -
                every one of them takes a new widget as soon as it is published.
                On the widget version they run today:{" "}
                {platformAppVersions.map(([v, n]) => `${v} (${n})`).join(" · ")}
                .
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary">
              NTP server (optional) - pushed to each device in the wave
              alongside the update, so the cohort re-syncs its clock against it.
              Native devices only; ignored where the shell cannot set it.
            </Typography>
            <TextField
              label="NTP server"
              value={draft.ntpServer}
              onChange={(e) =>
                setDraft({ ...draft, ntpServer: e.target.value })
              }
              placeholder="pool.ntp.org"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!canCreate || saving}
            onClick={create}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
